import React from 'react';
import { Code2, Globe, Smartphone, Construction as Connection, GitBranch, Apple as Api, Building2, Palette, WrenchIcon, TestTube2, Cloud, Cpu, Server, Brain, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const solutions = [
  {
    id: 'custom-software',
    icon: <Code2 className="w-8 h-8 text-yellow-400" />,
    title: "Custom Software Development",
    description: "Our custom software development services deliver high-performance solutions built precisely to meet your unique business needs. Whether you're looking to create desktop applications, SaaS platforms, or automation tools, our team of experts ensures your software aligns with your goals."
  },
  {
    id: 'web-development',
    icon: <Globe className="w-8 h-8 text-yellow-400" />,
    title: "Web Development",
    description: "We create dynamic, responsive, and high-performing websites and web applications. From eCommerce platforms to complex web portals, our web development team uses the latest technologies and frameworks to build robust digital experiences."
  },
  {
    id: 'mobile-app',
    icon: <Smartphone className="w-8 h-8 text-yellow-400" />,
    title: "Mobile App Development",
    description: "In today's mobile-first world, having a mobile app can give your business a competitive edge. We specialize in native and cross-platform mobile app development for iOS and Android, building feature-rich, intuitive, and scalable apps."
  },
  {
    id: 'software-integration',
    icon: <Connection className="w-8 h-8 text-yellow-400" />,
    title: "Software Integration",
    description: "Simplify workflows and unify data by integrating your software applications. Our software integration services enable seamless data flow between your various systems, making sure all your tools work together in harmony."
  },
  {
    id: 'devops',
    icon: <GitBranch className="w-8 h-8 text-yellow-400" />,
    title: "DevOps & Automation",
    description: "Accelerate your development process and improve reliability with our DevOps and automation services. By automating repetitive tasks and implementing CI/CD pipelines, we reduce errors and improve deployment speed."
  },
  {
    id: 'api-development',
    icon: <Api className="w-8 h-8 text-yellow-400" />,
    title: "API Development & Integration",
    description: "APIs are essential for connecting software applications and enhancing functionality. Our API development and integration services enable secure and efficient data exchange between platforms, whether you need a custom API or to integrate with existing ones."
  },
  {
    id: 'enterprise-software',
    icon: <Building2 className="w-8 h-8 text-yellow-400" />,
    title: "Enterprise Software Development",
    description: "Enterprises need software that scales with them. Our enterprise software development services create robust, scalable, and flexible solutions, tailored to manage complex business processes."
  },
  {
    id: 'ui-ux-design',
    icon: <Palette className="w-8 h-8 text-yellow-400" />,
    title: "UI/UX Design",
    description: "Design matters when it comes to user engagement. Our UI/UX design services prioritize ease of use, accessibility, and aesthetics to create engaging digital experiences. We design intuitive interfaces that guide users through applications seamlessly."
  },
  {
    id: 'maintenance',
    icon: <WrenchIcon className="w-8 h-8 text-yellow-400" />,
    title: "Maintenance & Support",
    description: "Our commitment to your software doesn't end after deployment. With our maintenance and support services, we ensure that your software remains functional, secure, and up-to-date with the latest technologies."
  },
  {
    id: 'quality-assurance',
    icon: <TestTube2 className="w-8 h-8 text-yellow-400" />,
    title: "Quality Assurance & Testing",
    description: "Delivering quality software requires rigorous testing. Our QA and testing services ensure that your software is reliable, secure, and performs as expected. We offer both manual and automated testing services."
  },
  {
    id: 'saas-development',
    icon: <Cloud className="w-8 h-8 text-yellow-400" />,
    title: "SaaS Development",
    description: "Looking to launch a Software-as-a-Service product? Our SaaS development services cover everything from product planning to deployment and maintenance. We build multi-tenant architectures, integrate subscription billing, and ensure scalability."
  },
  {
    id: 'iot-development',
    icon: <Cpu className="w-8 h-8 text-yellow-400" />,
    title: "IoT Development",
    description: "Bring your devices to life with our IoT development services. We build software that connects, monitors, and controls IoT devices, providing a seamless user experience. Our team specializes in IoT architecture, data management, and real-time analytics."
  },
  {
    id: 'hosting',
    icon: <Server className="w-8 h-8 text-yellow-400" />,
    title: "CoLo, Bare Metal, and VPS Hosting",
    description: "Our hosting solutions offer flexible, powerful infrastructure options tailored to your business needs. Whether you're looking for Colocation (CoLo), Bare Metal servers, or Virtual Private Servers (VPS), we provide secure, scalable environments."
  },
  {
    id: 'tech-consulting',
    icon: <Brain className="w-8 h-8 text-yellow-400" />,
    title: "Technology Consulting",
    description: "Need guidance on your digital transformation? Our technology consulting services help you make informed decisions about technology investments. We assess your business needs, evaluate your existing tech stack, and recommend solutions."
  }
];

const Solutions = () => {
  const location = useLocation();
  const currentHash = location.hash.slice(1);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Our Solutions</h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-12">
              Comprehensive software solutions tailored to your business needs.
              From custom development to enterprise solutions, we deliver excellence at every scale.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {solutions.map((solution) => (
                <a
                  key={solution.id}
                  href={`#${solution.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    currentHash === solution.id
                      ? 'bg-yellow-400 text-slate-900'
                      : 'bg-white/10 text-white hover:bg-yellow-400 hover:text-slate-900'
                  }`}
                >
                  {solution.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                id={solution.id}
                className={`bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 scroll-mt-32 ${
                  currentHash === solution.id ? 'bg-yellow-50' : ''
                }`}
              >
                <div className="mb-6">
                  {solution.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4">{solution.title}</h3>
                <p className="text-gray-600">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Choose Your Engagement Model</h2>
            <p className="text-lg text-gray-600">
              Whether you need a one-time project or ongoing development support,
              we have flexible engagement models to suit your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Project-Based Development */}
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="mb-6">
                <GitBranch className="w-12 h-12 text-yellow-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Project-Based Development</h3>
              <p className="text-gray-600 mb-6">
                From quick MVPs to full-scale enterprise solutions, our project-based
                model delivers complete solutions with clear timelines and budgets.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-600">
                  <ArrowRight className="w-5 h-5 text-yellow-400 mr-2" />
                  Launch Sprint (2-3 weeks)
                </li>
                <li className="flex items-center text-gray-600">
                  <ArrowRight className="w-5 h-5 text-yellow-400 mr-2" />
                  Custom Build Projects
                </li>
                <li className="flex items-center text-gray-600">
                  <ArrowRight className="w-5 h-5 text-yellow-400 mr-2" />
                  Platform Rescue & Modernization
                </li>
              </ul>
              <Link
                to="/projects"
                className="inline-flex items-center px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all group"
              >
                Explore Project Options
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Development as a Service */}
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="mb-6">
                <Cloud className="w-12 h-12 text-yellow-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Development as a Service</h3>
              <p className="text-gray-600 mb-6">
                Get a dedicated development team without the overhead. Scale up or down
                based on your needs with our flexible DaaS model.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-600">
                  <ArrowRight className="w-5 h-5 text-yellow-400 mr-2" />
                  Dedicated Development Team
                </li>
                <li className="flex items-center text-gray-600">
                  <ArrowRight className="w-5 h-5 text-yellow-400 mr-2" />
                  Flexible Resource Scaling
                </li>
                <li className="flex items-center text-gray-600">
                  <ArrowRight className="w-5 h-5 text-yellow-400 mr-2" />
                  Continuous Development & Support
                </li>
              </ul>
              <Link
                to="/daas"
                className="inline-flex items-center px-6 py-3 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
              >
                Learn About DaaS
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how our solutions can help you achieve your business goals.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Get Started
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;